import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import { createExperienceQueryKey } from '@/utils/queryKeys';
import type {
  RefreshSourceForExperienceMutation,
  RefreshSourceForExperienceMutationVariables,
} from './useRefreshSource.generated';

// How long the button stays disabled after a click. Mirrors
// SOURCE_REFRESH_COOLDOWN_MS on the server, which is authoritative — this only
// keeps the user from spending a request it would reject anyway.
export const SOURCE_REFRESH_COOLDOWN_MS = 60_000;

// Selects the fields a sync can change, so @normy updates the feed in place.
// The server runs the sync inline, so by the time this resolves syncedAt/error
// already describe the run that just finished.
const query = gql`
  mutation RefreshSourceForExperience(
    $input: RefreshSourceForExperienceInput!
  ) {
    refreshSourceForExperience(input: $input) {
      kind
      id
      title
      originalTitle
      color
      syncedAt
      error
    }
  }
`;

export const useRefreshSource = () => {
  const queryClient = useQueryClient();
  const { experience } = usePageExperienceContext();

  const { mutate, ...rest } = useMutation<
    RefreshSourceForExperienceMutation,
    QueryError,
    RefreshSourceForExperienceMutationVariables
  >({
    mutationFn: (variables) =>
      gqlClient.request<
        RefreshSourceForExperienceMutation,
        RefreshSourceForExperienceMutationVariables
      >(query, variables),
    // Invalidate on settle, not just success: a sync that fails partway can
    // still have written events. The experience key is a prefix of every
    // per-source events key, so this refreshes the preview too.
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: createExperienceQueryKey(experience.id),
      }),
  });

  const refreshSource = (
    sourceId: string,
    options?: MutateOptions<
      RefreshSourceForExperienceMutation,
      QueryError,
      RefreshSourceForExperienceMutationVariables
    >
  ) => mutate({ input: { experienceId: experience.id, sourceId } }, options);

  return { refreshSource, ...rest };
};
